import { NormalizeOAS, OASRequestParams, OASResponse } from 'fets';
import type assetLibraryOpenApi from '../asset-library-open-api';
import { AssetLibraryFetsApiClient } from '../asset-library-fets-client';
import { assertNever } from '../utils';
import { Guid } from '../../api/public/utils/Guid';
import type { AssetDetail } from '../asset-library-types';

const PATH = '/api/v2/asset/company/{companyId}/asset/{assetId}/refresh-processing-state';

type Params = {
  companyId: Guid;
  assetId: Guid;
};

/**
 * The asset after the server asked the video processor how its job ended. Still `processing` when the
 * processor could not say and the video is inside the processing timeout; null when the asset is gone.
 */
type Result = AssetDetail | null;

export type ApiFunction = (params: Params, signal?: AbortSignal) => Promise<Result>;

export const createRefreshAssetProcessingState = (apiClient: AssetLibraryFetsApiClient): ApiFunction => {
  const refreshAssetProcessingState = async (params: Params, signal?: AbortSignal): Promise<Result> => {
    const response = await apiClient[PATH].post({
      ...convertToRequest(params),
      ...(signal && { signal }),
    });

    return convertResponse(response);
  };

  return refreshAssetProcessingState;
};

type Request = OASRequestParams<NormalizeOAS<typeof assetLibraryOpenApi>, typeof PATH, 'post'>;
type Response = OASResponse<NormalizeOAS<typeof assetLibraryOpenApi>, typeof PATH, 'post'>;

const convertToRequest = (params: Params): Request => ({
  params: { companyId: params.companyId, assetId: params.assetId },
});

const convertResponse = async (response: Response): Promise<Result> => {
  const responseStatus = response.status;
  if (responseStatus === 404) {
    return null;
  }

  if (responseStatus !== 200) {
    return assertNever(responseStatus);
  }

  const data = await response.json();
  const result: AssetDetail = {
    id: data.id,
    name: data.name,
    size: data.size,
    uploadedAt: Number(data.uploadedAt),
    state: data.state,
    mediaUrl: data.mediaUrl,
    downloadUrl: data.downloadUrl,
    urlsExpireAt: Number(data.urlsExpireAt),
    thumbnailUrl: data.thumbnailUrl ?? null,
    dimensions: data.dimensions ?? null,
    attribution: data.attribution ?? null,
  };
  return result;
};
